LocalStorageService.$inject = [
  '$parse',
  'credentials'
]

export function LocalStorageService($parse, credentials) {
  // IMPORTANT: please have a look at jsImprovements.js where the Storage API is enhanced!!

  /**
   * global vars
   */
  var global = {
    supported: checkSupport()
  };

  /**
   * object that stores all functions that are only used within this service
   */
  var localFunctions = {
    validCredentials: function () {
      return credentials.userId !== 'anonymous';
    }
  };

  var api = {
    get: function (key) {
      if (global.supported && localFunctions.validCredentials()) {
        var userIdEncoded = encodeURI(credentials.userId);

        var userStorage = localStorage.getObject(userIdEncoded);

        if (angular.isDefined(userStorage) && userStorage != null) {
          var encodedKey = encodeURI(key);

          if (userStorage.hasOwnProperty(encodedKey)) {
            return decodeURI(userStorage[encodedKey]);
          }
        }
      }

      return null;
    },
    getInt: function (key) {
      var value = api.get(key);
      if (value !== null && value !== undefined) {
        return parseInt(value);
      } else {
        return value;
      }
    },
    /**
     * stores the given key-value pair for the current userId in local storage.
     * @param key
     * @param value
     * @returns {boolean}
     */
    set: function (key, value) {
      if (global.supported && localFunctions.validCredentials()) {
        // value is neither null nor an object
        if (value !== null && typeof value !== 'object') {
          var userIdEncoded = encodeURI(credentials.userId);

          var storageForUser = localStorage.getObject(userIdEncoded);

          if (storageForUser === null || !angular.isDefined(storageForUser)) {
            storageForUser = {};
          }

          storageForUser[encodeURI(key)] = encodeURI(value);

          try {
            setObject(userIdEncoded, storageForUser);
            return true;
          } catch (e) {
            console.warn(
              'localStorageService: error writing to localStorage',
              e
            );
          }
        }
      }

      return false;
    },
    removeItem: function (key) {
      if (global.supported && localFunctions.validCredentials()) {
        var userIdEncoded = encodeURI(credentials.userId);

        var userStorage = localStorage.getObject(userIdEncoded);

        if (angular.isDefined(userStorage) && userStorage != null) {
          var encodedKey = encodeURI(key);

          if (userStorage.hasOwnProperty(encodedKey)) {
            delete userStorage[encodedKey];

            setObject(userIdEncoded, userStorage);
          }
        }
      }
    },
    /**
     *
     * @param key
     * @returns {*}
     */
    getGlobal: function (key) {
      if (global.supported) {
        var valueFromStorage = localStorage.getItem(encodeURI(key));
        return valueFromStorage !== null ? decodeURI(valueFromStorage) : null;
      }
      return null;
    },
    /**
     * Sometimes a global key-value pair is needed. So this method provides this functionality
     * @param key
     * @param value
     * @returns {boolean}
     */
    setGlobal: function (key, value) {
      if (global.supported) {
        if (value !== null && typeof value !== 'object') {
          setItem(encodeURI(key), encodeURI(value));
          return true;
        }
      }
      return false;
    },
    removeItemGlobal: function (key) {
      if (global.supported) {
        localStorage.removeItem(encodeURI(key));
      }
    },
    /**
     * clears the full local storage for the current user.
     */
    clearAll: function () {
      if (global.supported) {
        localStorage.clear();
      }
    },
    /**
     * Bind - let's you directly bind a localStorage value to a $scope variable
     * @param {Angular $scope} $scope - the current scope you want the variable available in
     * @param {String} key - the name of the variable you are binding
     * @param {Object} opts - (optional) custom options like default value or unique store name
     * Here are the available options you can set:
     * * defaultValue: the default value
     * * storeName: add a custom store key value instead of using the scope variable name
     * @returns {*} - returns whatever the stored value is
     */
    bind: function ($scope, key, opts) {
      var defaultOpts = {
        defaultValue: '',
        storeName: ''
      };
      // Backwards compatibility with old defaultValue string
      if (angular.isString(opts)) {
        opts = angular.extend({}, defaultOpts, {defaultValue: opts});
      } else {
        // If no defined options we use defaults otherwise extend defaults
        opts = angular.isUndefined(opts)
          ? defaultOpts
          : angular.extend(defaultOpts, opts);
      }

      // Set the storeName key for the localStorage entry
      // use user defined in specified
      var storeName = opts.storeName || key;

      // If a value doesn't already exist store it as is
      if (!api.get(storeName)) {
        api.set(storeName, opts.defaultValue);
      }

      // If it does exist assign it to the $scope value
      $parse(key).assign($scope, api.get(storeName));

      // Register a listener for changes on the $scope value
      // to update the localStorage value
      $scope.$watch(
        key,
        function (val) {
          if (angular.isDefined(val)) {
            api.set(storeName, val);
          }
        },
        true
      );

      return api.get(storeName);
    },
    /**
     * Unbind - let's you unbind a variable from localStorage while removing the value from both
     * the localStorage and the local variable and sets it to null
     * @param $scope - the scope the variable was initially set in
     * @param key - the name of the variable you are unbinding
     * @param storeName - (optional) if you used a custom storeName you will have to specify it here as well
     */
    unbind: function ($scope, key, storeName) {
      storeName = storeName || key;
      $parse(key).assign($scope, null);
      $scope.$watch(key, function () {
      });
      api.removeItem(storeName);
    }
  };

  function setItem(key, value) {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      console.warn('localStorageService: error writing to localStorage', e);
      return false;
    }
  }

  function setObject(key, value) {
    try {
      localStorage.setObject(key, value);
      return true;
    } catch (e) {
      console.warn('localStorageService: error writing to localStorage', e);
      return false;
    }
  }

  function checkSupport() {
    var supported = true;

    if (typeof localStorage === 'undefined' || localStorage === null) {
      supported = false;
    } else {
      //try to store an item for safari private mode
      var testKey = '__' + Math.round(Math.random() * 1e7);

      try {
        localStorage.setItem(testKey, testKey);
        localStorage.removeItem(testKey);
      } catch (err) {
        supported = false;
      }
    }

    return supported;
  }

  return api;
}
